import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";
import formatDate from "../utils/formatDate";
import XLSX from "xlsx-js-style";

const VisitSupport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [productiveGT, setProductiveGT] = useState(500);

  const [reportData, setReportData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatMonthText = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    } catch (error) {
      console.error("Error formatting month text:", error);
      return "";
    }
  };

  const formatAsInputDate = (date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      const t = new Date();
      const dd = String(t.getDate()).padStart(2, "0");
      const mm = String(t.getMonth() + 1).padStart(2, "0");
      const yyyy = t.getFullYear();
      return `${yyyy}-${mm}-${dd}`;
    }
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const coerceToInputDate = (val, fallbackDate) => {
    if (!val) return formatAsInputDate(fallbackDate);
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const d = new Date(val);
    if (!isNaN(d.getTime())) return formatAsInputDate(d);
    return formatAsInputDate(fallbackDate);
  };

  const normalizeMonthTextToIso = (monthText) => {
    if (!monthText) return undefined;
    if (typeof monthText === "string" && /^\d{4}-\d{2}-\d{2}$/.test(monthText))
      return monthText;
    if (typeof monthText === "string" && /^\d{4}-\d{2}$/.test(monthText))
      return `${monthText}-01`;
    const parsed = new Date(monthText);
    if (!isNaN(parsed.getTime())) {
      const y = parsed.getFullYear();
      const m = String(parsed.getMonth() + 1).padStart(2, "0");
      return `${y}-${m}-01`;
    }
    try {
      const parts = String(monthText).replace(",", " ").trim().split(/\s+/);
      if (parts.length >= 2) {
        const monthName = parts[0].toLowerCase();
        const year = parseInt(parts[1], 10);
        const months = [
          "january",
          "february",
          "march",
          "april",
          "may",
          "june",
          "july",
          "august",
          "september",
          "october",
          "november",
          "december",
        ];
        const idx = months.indexOf(monthName);
        if (idx >= 0 && !isNaN(year)) {
          const mm = String(idx + 1).padStart(2, "0");
          return `${year}-${mm}-01`;
        }
      }
    } catch (e) { }
    return undefined;
  };

  // Safe date initialization
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);

    const formatDateLocal = (date) => {
      if (!date || !(date instanceof Date) || isNaN(date)) {
        const today = new Date();
        const d = String(today.getDate()).padStart(2, "0");
        const m = String(today.getMonth() + 1).padStart(2, "0");
        const y = today.getFullYear();
        return `${y}-${m}-${d}`;
      }
      const d = String(date.getDate()).padStart(2, "0");
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = date.getFullYear();
      return `${y}-${m}-${d}`;
    };

    setStartDate(formatDateLocal(firstDay));
    setEndDate(formatDateLocal(today));
  }, []);

  useEffect(() => {
    if (location.state?.fromDocChem) {
      const { filters, tableState, options } = location.state;
      setSelectedState(filters?.selectedState || "All");
      setSelectedDivision(filters?.selectedDivision || "All");
      setSelectedEmployee(filters?.selectedEmployee || "All");

      const today = new Date();
      const defaultStartDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );
      const defaultEndDate = today;

      const resolvedStart =
        filters?.startDate ??
        filters?.fromDate ??
        filters?.from_date ??
        filters?.start_date ??
        null;
      const resolvedEnd =
        filters?.endDate ??
        filters?.toDate ??
        filters?.to_date ??
        filters?.end_date ??
        null;

      setStartDate(coerceToInputDate(resolvedStart, defaultStartDate));
      setEndDate(coerceToInputDate(resolvedEnd, defaultEndDate));

      setProductiveGT(filters?.productiveGT || 500);
      setShowTable(tableState?.showTable || false);
      setReportData(tableState?.reportData || []);
      setStates(options?.states || [{ id: "All", name: "All" }]);
      setDivisions(options?.divisions || [{ id: "All", name: "All" }]);
      setEmployees(
        options?.employees || [{ id: "All", first_name: "All", last_name: "" }],
      );
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    try {
      let url = `${BASE_URL}/api/employees`;
      const params = {};
      if (stateId && stateId !== "All") params.state_id = stateId;
      if (divisionName && divisionName !== "All")
        params.division = divisionName;
      const res = await axios.get(url, {
        params: Object.keys(params).length ? params : undefined,
        headers: getAuthHeaders(),
      });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ]);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
    }
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates([{ id: "All", name: "All" }, ...response.data]);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All") url += `?state_id=${stateId}`;
      const divisionRes = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      console.error("Error loading divisions", err);
      setDivisions([{ id: "All", name: "All" }]);
    }
    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const handleGoClick = async () => {
    setIsLoading(true);
    setError(null);
    const startTime = Date.now();
    try {
      const params = {};
      if (selectedState && selectedState !== "All")
        params.state_id = selectedState;
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find((d) => d.id == selectedDivision);
        if (division) params.division = division.name;
      }
      if (selectedEmployee && selectedEmployee !== "All")
        params.employee_id = selectedEmployee;
      if (startDate) params.from_date = startDate;
      if (endDate) params.to_date = endDate;
      if (productiveGT) params.productive_gt = productiveGT;

      const response = await axios.get(
        `${BASE_URL}/api/combined-retailers/consolidated-employee-report`,
        {
          params: Object.keys(params).length ? params : undefined,
          headers: getAuthHeaders(),
        },
      );
      setReportData(response.data || []);
      setShowTable(true);
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
      setShowTable(false);
    } finally {
      const elapsed = Date.now() - startTime;
      const minimumDelay = 500;
      if (elapsed < minimumDelay) {
        setTimeout(() => setIsLoading(false), minimumDelay - elapsed);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleCellClick = (item, fieldName, extra = {}) => {
    let endMonthText = "";
    try {
      endMonthText = formatMonthText(endDate);
    } catch (error) {
      console.error("Error getting end month text:", error);
      const currentDate = new Date();
      endMonthText = currentDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    }

    const endMonthIso = normalizeMonthTextToIso(endMonthText);

    const baseApiParamsWithMonths = (overrides = {}) => {
      const params = {
        state_id: selectedState !== "All" ? selectedState : undefined,
        employee_id: item.user_id,
        start_month: endMonthIso || undefined,
        end_month: endMonthIso || undefined,
        ...(startDate
          ? { from_date: startDate, fromDate: startDate, startDate }
          : {}),
        ...(endDate ? { to_date: endDate, toDate: endDate, endDate } : {}),
        ...overrides,
      };
      Object.keys(params).forEach(
        (k) => params[k] === undefined && delete params[k],
      );
      return params;
    };

    const openDetailsWindow = (path, apiParams, viewType = "retailer") => {
      try {
        const query = new URLSearchParams();
        if (viewType) query.append("viewType", viewType);
        Object.keys(apiParams || {}).forEach((k) => {
          const v = apiParams[k];
          if (v !== undefined && v !== null) query.append(k, String(v));
        });
        query.append("_t", Date.now());
        const url = `${window.location.origin}${path}${query.toString() ? `?${query.toString()}` : ""}`;
        const w = window.open(url, "_blank", "width=1100,height=800");
        if (!w) alert("Please allow popups for this site to view details.");
      } catch (err) {
        console.error("Failed to open details window:", err);
      }
    };

    if (fieldName === "total_retailers") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        baseApiParamsWithMonths({
          visit_status: "all",
          is_manager_total_call: true,
          is_report: true,
        }),
        "all_retailers",
      );
      return;
    }
    if (fieldName === "total_calls") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        baseApiParamsWithMonths({
          visit_status: "visited",
          is_manager_total_call: true,
          productive_gt: productiveGT,
        }),
        "total_calls",
      );
      return;
    }
    if (
      fieldName === "not_visited_retailers" ||
      fieldName === "not_visited_chemist"
    ) {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        baseApiParamsWithMonths({
          visit_status: "not_visited",
          is_manager_total_call: true,
          is_report: true,
        }),
        "not_visited",
      );
      return;
    }
    if (
      fieldName === "total_retailer_visits" ||
      fieldName === "total_chemist_visits"
    ) {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        baseApiParamsWithMonths({
          visit_status: "visited",
          is_manager_total_call: true,
          is_report: true,
        }),
        "total_calls",
      );
      return;
    }
    if (fieldName === "productive_visits") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        baseApiParamsWithMonths({
          visit_status: "visited",
          visit_productivity: "productive",
          productive_gt: productiveGT,
          is_manager_total_call: true,
          is_report: true,
        }),
        "productive",
      );
      return;
    }
    if (fieldName === "unproductive_visits") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        baseApiParamsWithMonths({
          visit_status: "visited",
          visit_productivity: "unproductive",
          productive_gt: productiveGT,
          is_manager_total_call: true,
          is_report: true,
        }),
        "unproductive",
      );
      return;
    }
    if (fieldName === "total_stockists") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        {
          visit_status: "all",
          employee_id: item.user_id,
          ...(startDate
            ? { from_date: startDate, fromDate: startDate, startDate }
            : {}),
          ...(endDate ? { to_date: endDate, toDate: endDate, endDate } : {}),
        },
        "total_stockists",
      );
      return;
    }
    if (fieldName === "total_visited_stockists") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        {
          visit_status: "visited",
          employee_id: item.user_id,
          ...(startDate
            ? { from_date: startDate, fromDate: startDate, startDate }
            : {}),
          ...(endDate ? { to_date: endDate, toDate: endDate, endDate } : {}),
        },
        "total_visited_stockists",
      );
      return;
    }
    if (fieldName === "total_non_visited_stockists") {
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        {
          visit_status: "not_visited",
          employee_id: item.user_id,
          ...(startDate
            ? { from_date: startDate, fromDate: startDate, startDate }
            : {}),
          ...(endDate ? { to_date: endDate, toDate: endDate, endDate } : {}),
        },
        "total_non_visited_stockists",
      );
      return;
    }
    if (extra && extra.isPOB) {
      let monthNum, yearNum;
      try {
        const base = endDate ? new Date(endDate) : new Date();
        if (isNaN(base.getTime())) throw new Error("Invalid endDate");
        base.setDate(1);
        if (fieldName === "pobPrev") base.setMonth(base.getMonth() - 1);
        monthNum = base.getMonth() + 1;
        yearNum = base.getFullYear();
      } catch (error) {
        console.error("Error calculating POB dates:", error);
        const currentDate = new Date();
        monthNum = currentDate.getMonth() + 1;
        yearNum = currentDate.getFullYear();
      }
      openDetailsWindow(
        `/calldetails/${item.emp_id}`,
        { employee_id: item.emp_id, month: monthNum, year: yearNum },
        "pob",
      );
      return;
    }

    openDetailsWindow(
      `/calldetails/${item.emp_id}`,
      baseApiParamsWithMonths(),
      "retailer",
    );
  };

  const getMonthHeaders = () => {
    try {
      const to = endDate ? new Date(endDate) : new Date();
      const from = startDate ? new Date(startDate) : new Date();
      if (isNaN(to.getTime()) || isNaN(from.getTime()))
        throw new Error("Invalid dates");
      const currentMonth = to.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      const prevMonthDate = new Date(to);
      prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
      const prevMonth = prevMonthDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      return { currentMonth, prevMonth };
    } catch (error) {
      console.error("Error getting month headers:", error);
      const currentDate = new Date();
      const currentMonth = currentDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      const prevMonthDate = new Date(currentDate);
      prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
      const prevMonth = prevMonthDate.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      return { currentMonth, prevMonth };
    }
  };

  const getDynamicFields = () => {
    try {
      const base = endDate ? new Date(endDate) : new Date();
      if (isNaN(base.getTime())) throw new Error("Invalid endDate");
      const currentMonth = base.getMonth() + 1;
      const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
      return {
        pobCurrent: `pob_${currentMonth}`,
        sobCurrent: `sob_${currentMonth}`,
        pobPrev: `prev_pob_${prevMonth}`,
      };
    } catch (error) {
      console.error("Error getting dynamic fields:", error);
      return {
        pobCurrent: "pob_1",
        sobCurrent: "sob_1",
        pobPrev: "prev_pob_12",
      };
    }
  };

  const { currentMonth, prevMonth } = getMonthHeaders();
  const { pobCurrent, sobCurrent, pobPrev } = getDynamicFields();

  // FIX 1: totals useMemo was missing — restored
  const totals = React.useMemo(() => {
    if (!reportData || reportData.length === 0) return null;
    const sum = (field) =>
      reportData.reduce((acc, row) => acc + Number(row[field] || 0), 0);
    return {
      self_working: sum("self_working"),
      rsm_jw: sum("rsm_jw"),
      asm_jw: sum("asm_jw"),
      tsm_jw: sum("tsm_jw"),
      total_stockist: sum("total_stockist"),
      visited_stockist: sum("visited_stockist"),
      not_visited_stockist: sum("not_visited_stockist"),
      total_retailers: sum("total_retailers"),
      convencing_ratio: sum("convencing_ratio"),
      avg_pob_per_counter: sum("avg_pob_per_counter"),
      total_calls: sum("total_calls"),
      not_visited_retailers: sum("not_visited_retailers"),
      total_retailer_visits: sum("total_retailer_visits"),
      productive_visits: sum("productive_visits"),
      unproductive_visits: sum("unproductive_visits"),
      pob_target: sum("pob_target"),
      sob_target: sum("sob_target"),
      pob_diff: sum("pob_diff"),
      pobCurrent: sum(pobCurrent),
      sobCurrent: sum(sobCurrent),
      pobPrev: sum(pobPrev),
    };
  }, [reportData, pobCurrent, sobCurrent, pobPrev]);

  const handlePrint = () => {
    const tableElem = tableRef.current?.querySelector("table");
    if (!tableElem) return;
    const html = tableElem.outerHTML;
    const title = "Visit Support Report";
    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;
    w.document.write(
      `<html><head><title>${title}</title><style>body{font-family:Arial;padding:16px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ccc;padding:6px;text-align:center}thead th{background:#333;color:#fff}</style></head><body><h2>${title}</h2>${html}<script>window.onload=function(){window.print();}</script></body></html>`,
    );
    w.document.close();
  };

const handleExport = async () => {
  try {

    if (!reportData.length) {
      setError("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};

    const headers = [
      "S.No",
      "Emp ID",
      "State",
      "Sub State",
      "Employee Name",
      "Designation",
      "HQ",
      "Division",
      "DOJ",
      "Resignation Date",
      "Manager",
      "Self Working",
      "RSM JW",
      "ASM JW",
      "TSM JW",
      "Total Stockist",
      "Visited Stockist",
      "Not Visited Stockist",
      "Total Retailers",
      "Total Calls",
      "Not Visited Retailers",
      "Total Retailer Visits",
      `Productive Visits (≥ ${productiveGT})`,
      "Unproductive Visits",
      "Convincing Ratio",
      "Average POB per Counter",
      "POB Target Monthly",
      `${currentMonth} POB`,
      "SOB Target Monthly",
      `${currentMonth} SOB`,
      `${prevMonth} POB`,
      "POB Diff",
    ];

    const headerStyle = {
      font: {
        bold: true,
        color: { rgb: "FFFFFF" },
      },
      fill: {
        fgColor: { rgb: "2563EB" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const rowStyle = {
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const setDateCell = (r, c, rawVal, s) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (rawVal) {
        try {
          const date = new Date(
            /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
              ? `${rawVal}T00:00:00`
              : rawVal
          );
          if (!isNaN(date.getTime())) {
            ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
            return;
          }
        } catch {}
      }
      ws[addr] = { v: "", t: "s", s };
    };

    headers.forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: 0,
        c: colIndex,
      });

      ws[cellAddress] = {
        v: header,
        t: "s",
        s: headerStyle,
      };
    });

    const DOJ_COL = 8;
    const RESIGN_COL = 9;

    reportData.forEach((item, index) => {
      const empRaw = String(item.user_id ?? "").trim();
      const empIsNum = /^\d+$/.test(empRaw);
      const empID = empIsNum ? Number(empRaw) : empRaw;

      const rowData = [
        index + 1,
        empID,
        item.state || "",
        item.sub_state || "",
        item.employee || "",
        item.designation || "",
        item.hq || "",
        item.division || "",
        null,
        null,
        item.manager || "",

        Number(item.self_working || 0),
        Number(item.rsm_jw || 0),
        Number(item.asm_jw || 0),
        Number(item.tsm_jw || 0),

        Number(item.total_stockist || 0),
        Number(item.visited_stockist || 0),
        Number(item.not_visited_stockist || 0),

        Number(item.total_retailers || 0),
        Number(item.total_calls || 0),
        Number(item.not_visited_retailers || 0),
        Number(item.total_retailer_visits || 0),

        Number(item.productive_visits || 0),
        Number(item.unproductive_visits || 0),

        Number(item.convencing_ratio || 0),
        Number(item.avg_pob_per_counter || 0),

        Number(item.pob_target || 0),
        Number(item[pobCurrent] || 0),

        Number(item.sob_target || 0),
        Number(item[sobCurrent] || 0),

        Number(item[pobPrev] || 0),
        Number(item.pob_diff || 0),
      ];

      rowData.forEach((value, colIndex) => {
        if (colIndex === DOJ_COL || colIndex === RESIGN_COL) return;

        const cellAddress = XLSX.utils.encode_cell({
          r: index + 1,
          c: colIndex,
        });

        ws[cellAddress] = {
          v: value,
          t:
            colIndex === 1
              ? empIsNum
                ? "n"
                : "s"
              : typeof value === "number"
                ? "n"
                : "s",
          s: rowStyle,
        };
      });

      setDateCell(index + 1, DOJ_COL, item.doj, rowStyle);
      setDateCell(index + 1, RESIGN_COL, item.resignation_date, rowStyle);
    });

    const totalRow = reportData.length + 2;

    ws[`A${totalRow}`] = {
      v: "TOTAL",
      t: "s",
      s: headerStyle,
    };

    for (let col = 11; col <= 31; col++) {
      const colLetter = XLSX.utils.encode_col(col);

      ws[`${colLetter}${totalRow}`] = {
        f: `SUM(${colLetter}2:${colLetter}${reportData.length + 1})`,
        t: "n",
        s: headerStyle,
      };
    }

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: {
        r: totalRow,
        c: headers.length - 1,
      },
    });

    ws["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 15 },
      { wch: 18 },
      { wch: 28 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
      { wch: 16 },
      { wch: 24 },

      ...Array.from(
        { length: 21 },
        () => ({ wch: 16 })
      ),
    ];

    ws["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
    };

    ws["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}1`,
    };

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Visit Support"
    );

    XLSX.writeFile(
      wb,
      `visit_support_${startDate || "all"}_${endDate || "all"}.xlsx`,
      {
        compression: true,
      }
    );

  } catch (err) {
    console.error("Export failed:", err);
  }
};

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <Loader onCancel={() => setIsLoading(false)} />
        </div>
      )}

      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Joint </span>
          <span className="text-blue-500">Working</span>
        </h1>

        <div className="flex flex-col items-center gap-3">
          <div className="grid grid-cols-2 gap-x-16 gap-y-3">
            {/* Row 1 */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-36 text-right">
                State
              </label>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-xs w-80"
                onChange={(e) => handleStateChange(e.target.value)}
                value={selectedState}
              >
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-24 text-right">
                Division
              </label>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-xs w-80"
                onChange={(e) => handleDivisionChange(e.target.value)}
                value={selectedDivision}
              >
                {divisions.map((div) => (
                  <option key={div.id} value={div.id}>
                    {div.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Row 2 */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-36 text-right">
                Employee
              </label>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-xs w-80"
                onChange={(e) => setSelectedEmployee(e.target.value)}
                value={selectedEmployee}
              >
                {employees.map((emp) =>
                  emp.id !== "All" ? (
                    <option key={emp.id} value={emp.id}>
                      {`${emp.first_name} ${emp.last_name} ${emp.employee} (${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name})`}
                    </option>
                  ) : (
                    <option key="All" value="All">
                      All
                    </option>
                  ),
                )}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-24 text-right">
                Start-Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-1 py-1 border border-gray-300 rounded text-xs w-[108px]"
              />
              <label className="text-xs text-gray-600 whitespace-nowrap">
                End-Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-1 py-1 border border-gray-300 rounded text-xs w-[108px]"
              />
            </div>

            {/* Row 3 */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 whitespace-nowrap w-36 text-right">
                Productive Greater Than
              </label>
              <input
                type="number"
                className="px-2 py-1 border border-gray-300 rounded text-xs w-24"
                value={productiveGT}
                onChange={(e) => setProductiveGT(e.target.value)}
              />
            </div>
          </div>

          {/* GO Button */}
          <CustomButton variant="go" onClick={handleGoClick}>
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {showTable && (
        <div className="mt-4 bg-white border border-gray-300 rounded-lg p-4">
          <div className="border rounded shadow" ref={tableRef}>
            <table className="unlock-dcr-leave-table text-sm">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="p-2 border">S.No</th>
                  <th className="p-2 border">Emp ID</th>
                  <th className="p-2 border">State</th>
                  <th className="p-2 border">Sub State</th>
                  <th className="p-2 border">Employee Name</th>
                  <th className="p-2 border">Designation</th>
                  <th className="p-2 border">HQ</th>
                  <th className="p-2 border">Division</th>
                  <th className="p-2 border">DOJ</th>
                  <th className="p-2 border">Manager</th>
                  <th className="p-2 border">Self Working</th>
                  <th className="p-2 border">RSM JW</th>
                  <th className="p-2 border">ASM JW</th>
                  <th className="p-2 border">TSM JW</th>
                  <th className="p-2 border">Total Stockist</th>
                  <th className="p-2 border">Visited Stockist</th>
                  <th className="p-2 border">Not Visited Stockist</th>
                  <th className="p-2 border">Total Retailers</th>
                  <th className="p-2 border">Total Calls</th>
                  <th className="p-2 border">Not Visited Retailers</th>
                  <th className="p-2 border">Total Retailer Visits</th>
                  <th className="p-2 border">
                    Productive Visits (≥ {productiveGT})
                  </th>
                  <th className="p-2 border">Unproductive Visits</th>
                  <th className="p-2 border">Convincing Ratio</th>
                  <th className="p-2 border">Average POB per Counter</th>
                  <th className="p-2 border">POB Target Monthly</th>
                  <th className="p-2 border">{currentMonth} POB</th>
                  <th className="p-2 border">SOB Target Monthly</th>
                  <th className="p-2 border">{currentMonth} SOB</th>
                  <th className="p-2 border">{prevMonth} POB</th>
                  <th className="p-2 border">POB Diff</th>
                </tr>
              </thead>
              <tbody className="text-center divide-y divide-gray-100 bg-white">
                {reportData.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 even:bg-gray-100 transition-colors"
                  >
                    <td className="p-2 border">{index + 1}</td>
                    <td className="p-2 border">{item.user_id || "-"}</td>
                    <td className="p-2 border">{item.state || ""}</td>
                    <td className="p-2 border">{item.sub_state || ""}</td>
                    <td className="p-2 border">{item.employee || ""}</td>
                    <td className="p-2 border">{item.designation || ""}</td>
                    <td className="p-2 border">{item.hq || ""}</td>
                    <td className="p-2 border">{item.division || ""}</td>
                    <td className="p-2 border">{formatDate(item.doj)}</td>
                    <td className="p-2 border">{item.manager || ""}</td>
                    <td className="p-2 border">{item.self_working}</td>
                    <td className="p-2 border">{item.rsm_jw}</td>
                    <td className="p-2 border">{item.asm_jw}</td>
                    <td className="p-2 border">{item.tsm_jw}</td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() => handleCellClick(item, "total_stockists")}
                    >
                      {item.total_stockist}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "total_visited_stockists")
                      }
                    >
                      {item.visited_stockist}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "total_non_visited_stockists")
                      }
                    >
                      {item.not_visited_stockist}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() => handleCellClick(item, "total_retailers")}
                    >
                      {item.total_retailers}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() => handleCellClick(item, "total_calls")}
                    >
                      {item.total_calls}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "not_visited_retailers")
                      }
                    >
                      {item.not_visited_retailers}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "total_retailer_visits")
                      }
                    >
                      {item.total_retailer_visits}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() => handleCellClick(item, "productive_visits")}
                    >
                      {item.productive_visits}
                    </td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "unproductive_visits")
                      }
                    >
                      {item.unproductive_visits}
                    </td>
                    {/* FIX 2: Math.round only takes 1 argument — use toFixed(2) instead */}
                    <td className="p-2 border">
                      {parseFloat(item.convencing_ratio || 0).toFixed(2)}
                    </td>
                    <td className="p-2 border">
                      {parseFloat(item.avg_pob_per_counter || 0).toFixed(2)}
                    </td>
                    <td className="p-2 border">{item.pob_target}</td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "pobCurrent", {
                          isPOB: true,
                          monthLabel: currentMonth,
                        })
                      }
                    >
                      {item[pobCurrent] || 0}
                    </td>
                    <td className="p-2 border">{item.sob_target}</td>
                    <td className="p-2 border">{item[sobCurrent] || 0}</td>
                    <td
                      className="p-2 border text-blue-600 cursor-pointer hover:underline"
                      onClick={() =>
                        handleCellClick(item, "pobPrev", {
                          isPOB: true,
                          monthLabel: prevMonth,
                        })
                      }
                    >
                      {item[pobPrev] || 0}
                    </td>
                    <td className="p-2 border">{item.pob_diff}</td>
                  </tr>
                ))}
              </tbody>

              {/* FIX 3: tfoot was missing — restored */}
              {totals && (
                <tfoot className="bg-blue-500 font-semibold text-center text-white">
                  <tr>
                    <td colSpan="10" className="p-2 border text-right">
                      TOTAL
                    </td>
                    <td className="p-2 border">{totals.self_working}</td>
                    <td className="p-2 border">{totals.rsm_jw}</td>
                    <td className="p-2 border">{totals.asm_jw}</td>
                    <td className="p-2 border">{totals.tsm_jw}</td>
                    <td className="p-2 border">{totals.total_stockist}</td>
                    <td className="p-2 border">{totals.visited_stockist}</td>
                    <td className="p-2 border">
                      {totals.not_visited_stockist}
                    </td>
                    <td className="p-2 border">{totals.total_retailers}</td>
                    <td className="p-2 border">{totals.total_calls}</td>
                    <td className="p-2 border">
                      {totals.not_visited_retailers}
                    </td>
                    <td className="p-2 border">
                      {totals.total_retailer_visits}
                    </td>
                    <td className="p-2 border">{totals.productive_visits}</td>
                    <td className="p-2 border">{totals.unproductive_visits}</td>
                    <td className="p-2 border">{Math.round(totals.convencing_ratio)}</td>
                    <td className="p-2 border">{Math.round(totals.avg_pob_per_counter)}</td>
                    <td className="p-2 border">{totals.pob_target}</td>
                    <td className="p-2 border">{totals.pobCurrent}</td>
                    <td className="p-2 border">{totals.sob_target}</td>
                    <td className="p-2 border">{totals.sobCurrent}</td>
                    <td className="p-2 border">{totals.pobPrev}</td>
                    <td className="p-2 border">{totals.pob_diff}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              label="Export"
              variant="export"
              onClick={handleExport}
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitSupport;
